import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnInit, Output, ViewChild, inject } from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms'
import {BehaviorSubject, debounceTime, distinctUntilChanged, forkJoin, Observable, of, switchMap, zip} from "rxjs";
import {catchError, filter, map} from "rxjs/operators";
import {isArray} from "ngx-bootstrap/chronos";
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import {BaseApiEndpointService} from '../../services/base-endpoint/base-endpoint.service';
import {ListItem} from '../../types/list-item.type';
import {ServiceProviderHub, SingleResponse} from '@generated-backend-types';
import {sortListByGivenKey} from '../../utils/sort-list-by-given-key';
import {LocaleService} from '../../services/locale-service/locale.service';
import UuidAttributeData = ServiceProviderHub.App.Data.Meta.UuidAttributeData;
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-multi-select-with-service[service][searchOnField]',
  templateUrl: './multi-select-with-service.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: MultiSelectWithServiceComponent,
    multi: true
  }],
  imports: [TooltipModule, NgMultiSelectDropDownModule, FormsModule, NgIf, TranslatePipe, NgForOf, KeyValuePipe],
  standalone: true
})
export class MultiSelectWithServiceComponent<Model extends BaseResource, Data = Model> implements OnInit, ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private readonly localeService = inject(LocaleService);
  private fb = inject(FormBuilder);


  @Input() public label!: string
  @Input() public inline = false

  @Input() public small = false
  @Input() public searchOnField?: string;
  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public prefix?: string
  @Input() public service!: BaseApiEndpointService<Model, Data>
  @Input() public selectionMode: 'single_id' | 'multiple_models' | 'single_model' = 'multiple_models'
  @Input() public sort = '-state'
  @Input() public tooltipText?: string
  @Input() public filters?: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }
  @Input() public preselectByAttribute?: keyof Model;
  @Output() public selectedFullModels: EventEmitter<null | string | Model[]> = new EventEmitter<null | string | Model[]>();
  @Input() public placeholder?: string
  public selectedItems: ListItem[] = []
  public readonly Infinity = Infinity;
  /**
   * @see https://github.com/nileshpatel17/ng-multiselect-dropdown#settings
   */
  public multiSelectSearchFilterChanged: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined)
  public multiSelectIsLoading = false;
  public allSelectOptions: (ListItem & { model: Model })[] = [];
  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;
  @Input()
  public formControl?: FormControl<Model[] | string | null | undefined | string[]> | FormControl<UuidAttributeData[]>
  @Input()
  public formControlName!: string;
  @Input()
  public closeDropDownOnSelection = false;
  private preselectInitialized = false;

  constructor() {
    this.multiSelectSearchFilterChanged.pipe(
      filter(d => d !== undefined),
      distinctUntilChanged(),
      debounceTime(400),
      map(d => d as string)).subscribe(
      d => this.loadData(d)
    )
  }

  @Input()
  public set reloadEmitter(e: EventEmitter<void>) {
    e.subscribe(() => {
      this.reload();
    })
  }

  public get control(): FormControl<Model[] | string | null | undefined | string[]> {

    if (this.formControl) {
      return <FormControl<Model[] | string | null | undefined | string[]>>this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl<Model[] | string | null | undefined | string[]>;
    }
    return new FormControl<Model[] | string | null | undefined | string[]>(null)

  }

  get controlContainer(): ControlContainer | null {
    return this.injector.get(ControlContainer, null);
  }

  public selectionChanged() {
    this.control?.markAsTouched()

    if (this.selectionMode === 'multiple_models' || this.selectionMode === 'single_model') {

      if (this.selectedItems.length === 0) {
        this.selectedFullModels.emit([]);
        this.control?.setValue([])
        return;
      }
      of(this.selectedItems).pipe(
        map(d => d.map(f => f.id as string)),
        map(ids => ids.map(id => this.service.get(id) as Observable<SingleResponse<Model>>)),
        switchMap(observables => forkJoin(observables)), // Use switchMap and forkJoin
        map((responses: SingleResponse<Model>[]) => responses.map(response => response.data)),
      ).subscribe(
        (items: Model[]) => {
          this.selectedFullModels.emit(items);
          this.control?.setValue(items)
        }
      )
      return;

    } else if (this.selectionMode === 'single_id') {
      const val = this.selectedItems.length === 1 ? (this.selectedItems[0].id as string) : null;
      this.selectedFullModels.emit(val);
      this.control?.setValue(val)
      return
    }
    console.error('selection mode not implemented');


  }

  public ngOnInit() {
    this.multiSelectSearchFilterChanged?.next('');
    this.control?.valueChanges.subscribe(
      v => this.updateSelectedItemsForControlChange(v)
    )
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: (v: unknown) => void): void {
    this.control?.valueChanges.pipe(distinctUntilChanged()).subscribe(v => fn(v))
  }

  writeValue(obj: Model[] | string | null | undefined | string[]): void {

    if (this.control && this.control.value != obj) {
      this.control?.setValue(obj, {emitEvent: true});
    }


  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  public reload() {

    this.loadData('')

  }

  /**
   * aktualisiert die selectedItems für den multiselect
   */
  public updateSelectedItemsForControlChange(currentlySelected: undefined | null | string | string[] | Model[]): void {
    this.cdr.markForCheck();

    if (!currentlySelected) {
      // aktuell nichts ausgewählt
      this.selectedItems = [];
      this.cdr.detectChanges()
      return
    } else if (typeof currentlySelected === 'string') {
      // eine id ausgewählt
      this.service?.get(currentlySelected).pipe(map(r => this.service.modelToMultiSelectItem(r.data))).subscribe(
        (d => {
          this.selectedItems = [d]
          this.multiSelectIsLoading = false;
          this.cdr.detectChanges()
        })
      )
      this.cdr.markForCheck();
      return;
    } else if (isArray(currentlySelected) && currentlySelected.length > 0 && typeof currentlySelected[0] === 'string') {

      const requests = (currentlySelected as string[]).map((id) => this.service?.get(id));

      forkJoin(requests).pipe(
        map((responses) => responses.map((r) => this.service.modelToMultiSelectItem(r.data)))
      ).subscribe((data) => {
        this.selectedItems = data;
        this.cdr.markForCheck();
      });
      return;
    }


    if (currentlySelected?.length === 0) {
      this.multiSelectIsLoading = false;
      this.selectedItems = [];
      this.cdr.markForCheck();
      return;
    }

    zip(
      (currentlySelected as Model[])?.map((v) => {
        const foundIndex = this.allSelectOptions.findIndex(listValue => {
          return listValue !== null && listValue['id'] && listValue['id'] === v.id
        });
        if (foundIndex > -1) {
          return of(this.allSelectOptions[foundIndex].model)
        }
        // item not found in list! => get it from service
        return this.service?.get(v.id).pipe(
          map(e => e.data),
          catchError(e => {
            if (e.status === 404) {
              // if item is not found
              console.error(e)
              return of(null)
            }
            throw e;

          })
        )
      }))
      .subscribe(
        (d) => {
          this.selectedItems = d.filter(d => d !== null).map(model => this.service.modelToMultiSelectItem(model as Model))
          this.multiSelectIsLoading = false;
          this.cdr.detectChanges();

        }
      )
  }

  public loadData(query: string) {

    of(query).pipe(
      switchMap((query: string) => {

        this.multiSelectIsLoading = true;
        const params: {
          [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
        } = {};
        if (this.searchOnField) {
          params['filter[' + this.searchOnField + ']'] = query;
        }

        params['sort'] = this.sort;
        if (this.filters) {
          const converted = this.convertFilters(this.filters)
          converted.forEach(p => {
            params[p.key] = p.value
          })
        }
        return this.service.getPage(1, params).pipe(map(d => d.data));

      }),
      map((list) => {
        return list.map((model) => this.service.modelToMultiSelectItem(model))
      }),
    )
      .subscribe(
        {

          next: (allModels) => {
            this.multiSelectIsLoading = false
            this.allSelectOptions = sortListByGivenKey<Model>(allModels, this.service.multiSelectSortBy, this.localeService);
            this.updateSelectedItemsForControlChange(this.control.getRawValue())
            this.initPreselect(allModels);
            this.cdr.detectChanges();
          },
          error: () => {
            console.error('error when loading data from service');
            this.multiSelectIsLoading = false;
            this.control?.setErrors({
              serverError: 'Invalid Data received from Server!'
            }, {emitEvent: true})
            this.control?.markAsDirty()
            this.control?.markAsTouched()
            this.cdr.markForCheck()
          }
        }
      )
  }

  private convertFilters(filters: {
    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
  }): { key: string, value: string }[] {
    const result: { key: string, value: string }[] = [];
    for (const key in filters) {
      if (filters[key]) {
        const value = filters[key];

        if (Array.isArray(value)) {
          for (const element of value) {
            result.push({key: 'filter[' + key + ']', value: element});
          }
        } else {
          result.push({key: 'filter[' + key + ']', value: value.toString()});
        }
      }
    }
    return result;
  }

  private initPreselect(list: (ListItem & { model: Model })[]) {
    if (!this.preselectInitialized && this.preselectByAttribute !== undefined) {
      this.preselectInitialized = true;
      const tempList = [...list];
      const filteredList = tempList.filter(item => {
        return item.model[this.preselectByAttribute as (keyof Model)];
      });
      switch (this.selectionMode) {
        case "single_id":
          // eslint-disable-next-line no-case-declarations
          const val = filteredList.length === 1 ? (filteredList[0].id as string) : null;
          this.selectedFullModels.emit(val);
          this.control?.setValue(val)
          break;
        case "single_model":
          // eslint-disable-next-line no-case-declarations
          const singleModel = filteredList.length > 0 ? [filteredList[0].model] : [];
          this.selectedFullModels.emit(singleModel);
          this.control?.setValue(singleModel)
          break;
        case "multiple_models":
          // eslint-disable-next-line no-case-declarations
          const multipleModels = filteredList.length > 0 ? [...filteredList.map(item => item.model)] : [];
          this.selectedFullModels.emit(multipleModels);
          this.control?.setValue(multipleModels)
          break;
      }

    }
  }
}
